import React, { useState, useEffect } from 'react';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';

const LastTenPlayedModal = ({ onClose, onShowDetail }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTab, setUserTab] = useState(null); // null = combined, username for specific user
  const [users, setUsers] = useState([]);

  // Load users from stats API on mount
  useEffect(() => {
    api.get('/api/stats').then(data => {
      if (data.user_stats) {
        setUsers(Object.entries(data.user_stats).map(([username, userData]) => ({
          username,
          displayName: userData.display_name,
          color: userData.color || THEME.success,
          icon: userData.icon
        })));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const endpoint = userTab ? `/api/games/recent-sessions?user=${userTab}` : '/api/games/recent-sessions';
    api.get(endpoint).then(data => {
      setGames(data.games || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userTab]);

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Build tabs dynamically from users
  const tabs = [
    { value: null, label: 'Combined', color: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` },
    ...users.map(u => ({ value: u.username, label: `${u.icon || ''} ${u.displayName}`, color: u.color }))
  ];

  const currentTabColor = userTab ? (users.find(u => u.username === userTab)?.color || THEME.success) : `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }} onClick={onClose}>
      <div style={{ background: `linear-gradient(145deg, ${THEME.bgElevated}, ${THEME.bgCard})`, borderRadius: '20px', padding: '24px', width: '95%', maxWidth: '500px', maxHeight: '85vh', overflow: 'auto', border: `1px solid ${THEME.border}`, boxShadow: `0 25px 80px ${THEME.shadowHeavy}`, animation: 'scaleIn 0.2s ease' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>🕐</span> Last 10 Played
          </h2>
          <button onClick={onClose} style={{ background: THEME.bgHover, border: 'none', borderRadius: '8px', padding: '8px 12px', color: THEME.textMuted, cursor: 'pointer' }}>✕</button>
        </div>

        {/* User Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {tabs.map(tab => (
            <button
              key={tab.value || 'combined'}
              onClick={() => setUserTab(tab.value)}
              className="press-effect"
              style={{
                flex: 1,
                background: userTab === tab.value ? (typeof tab.color === 'string' && tab.color.startsWith('linear') ? tab.color : tab.color) : THEME.bg,
                color: userTab === tab.value ? '#000' : (tab.value ? tab.color : THEME.textMuted),
                border: `1px solid ${userTab === tab.value ? 'transparent' : THEME.border}`,
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
        ) : games.length === 0 ? (
          <div style={{ textAlign: 'center', color: THEME.textMuted, padding: '40px' }}>No recent sessions recorded</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {games.map((game, idx) => (
              <div key={game.id} onClick={() => { onShowDetail(game); onClose(); }} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '12px', background: THEME.bg, borderRadius: '12px', cursor: 'pointer', border: `1px solid ${THEME.border}`, transition: 'all 0.15s' }}>
                <div style={{ width: '32px', height: '32px', background: currentTabColor, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#000', fontSize: '14px' }}>{idx + 1}</div>
                <img src={game.header_image} alt="" style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '8px' }} onError={e => { e.target.style.background = THEME.bgElevated; }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name}</div>
                  <div style={{ color: THEME.textMuted, fontSize: '12px' }}>{formatTimeAgo(game.last_session_at)}</div>
                </div>
                <div style={{ color: THEME.textSecondary, fontSize: '12px', textAlign: 'right' }}>
                  {game.total_session_minutes ? `${Math.round(game.total_session_minutes / 60)}h total` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LastTenPlayedModal;
